// Search input with up/down navigation + suggestions dropdown built from a 2D values array

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  useFloating,
  offset,
  flip,
  shift,
  size,
  FloatingPortal,
} from '@floating-ui/react';
import './SearchInput.scss';

export default function SearchInput({
  values,
  onMatch,
  placeholder = 'Search',
  className,
  small,
  disabled,
  externalSearchRequest,
}) {
  // Build per-row labels like: "John, 12U, Hawks" (only non-empty strings)
  const rows = useMemo(() => {
    return values.map((row, index) => {
      const parts = row.map((v) => String(v ?? '').trim()).filter(Boolean);
      const label = parts.join(', ');
      return {
        index,
        label,
        lower: label.toLowerCase(),
      };
    });
  }, [values]);

  const [query, setQuery] = useState('');
  const [lastIndex, setLastIndex] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    setLastIndex(null);
  }, [values]);

  const normalizedQuery = query.trim().toLowerCase();

  const filteredOptions = useMemo(() => {
    if (!normalizedQuery) return [];
    return rows.filter((r) => r.label && r.lower.includes(normalizedQuery));
  }, [rows, normalizedQuery]);

  const shouldShowDropdown =
    !disabled &&
    isOpen &&
    Boolean(normalizedQuery) &&
    filteredOptions.length > 0;

  // Floating UI dropdown
  const { refs, floatingStyles, update } = useFloating({
    placement: 'bottom-start',
    strategy: 'fixed',
    middleware: [
      offset(8),
      flip({ fallbackPlacements: ['top-start', 'top-end'] }),
      shift({ padding: 8 }),
      size({
        apply({ rects, elements, availableHeight }) {
          Object.assign(elements.floating.style, {
            minWidth: `${Math.round(rects.reference.width)}px`,
            maxWidth: 'min(92vw, 640px)',
          });

          const ul = elements.floating.querySelector('.search-options');
          const maxListH = Math.max(availableHeight - 2, 96);
          if (ul) {
            Object.assign(ul.style, {
              maxHeight: `${Math.floor(maxListH)}px`,
              overflowY: 'auto',
            });
          }
        },
      }),
    ],
  });

  const setReference = useCallback(
    (node) => {
      refs.setReference(node);
      inputRef.current = node;
    },
    [refs],
  );

  useEffect(() => {
    if (shouldShowDropdown) update?.();
  }, [shouldShowDropdown, update, filteredOptions.length, query]);

  // Close on outside click (reference + floating)
  useEffect(() => {
    if (!shouldShowDropdown) return;

    const onPointerDown = (e) => {
      const t = e.target;
      const refEl = refs.reference.current;
      const floatEl = refs.floating.current;

      if (refEl && refEl.contains(t)) return;
      if (floatEl && floatEl.contains(t)) return;

      setIsOpen(false);
    };

    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [shouldShowDropdown, refs.reference, refs.floating]);

  useEffect(() => {
    if (!externalSearchRequest) return;

    const nextValue = String(externalSearchRequest.value ?? '').trim();
    setQuery(nextValue);
    setLastIndex(null);
    onMatch(null);
    setIsOpen(Boolean(nextValue));

    requestAnimationFrame(() => {
      const inputEl = inputRef.current;
      if (!inputEl) return;
      inputEl.focus();

      const caretPos = inputEl.value.length;
      if (typeof inputEl.setSelectionRange === 'function') {
        inputEl.setSelectionRange(caretPos, caretPos);
      }
    });
  }, [externalSearchRequest, onMatch]);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    setLastIndex(null);

    if (!val.trim()) {
      onMatch(null);
      setIsOpen(false);
      return;
    }

    setIsOpen(true);
  };

  const runSearch = useCallback(
    (direction) => {
      if (!normalizedQuery) {
        onMatch(null);
        setLastIndex(null);
        return;
      }

      const total = rows.length;
      const step = direction === 'down' ? 1 : -1;

      let startIndex;
      if (lastIndex === null || lastIndex === undefined) {
        startIndex = direction === 'down' ? 0 : total - 1;
      } else {
        startIndex = (lastIndex + step + total) % total;
      }

      let index = startIndex;
      let foundIndex = null;

      for (let i = 0; i < total; i += 1) {
        const match = rows[index]?.lower?.includes(normalizedQuery);
        if (match) {
          foundIndex = index;
          break;
        }
        index = (index + step + total) % total;
      }

      setLastIndex(foundIndex);
      onMatch(foundIndex);
    },
    [normalizedQuery, rows, lastIndex, onMatch],
  );

  const selectOption = useCallback(
    (opt) => {
      setLastIndex(opt.index);
      onMatch(opt.index);
      setIsOpen(false);
    },
    [onMatch],
  );

  const selectAdjacentOption = useCallback(
    (direction) => {
      if (!shouldShowDropdown || filteredOptions.length === 0) return;

      const step = direction === 'down' ? 1 : -1;
      const currentPos = filteredOptions.findIndex(
        (opt) => opt.index === lastIndex,
      );
      const nextPos =
        currentPos === -1
          ? direction === 'down'
            ? 0
            : filteredOptions.length - 1
          : (currentPos + step + filteredOptions.length) %
            filteredOptions.length;
      const opt = filteredOptions[nextPos];

      if (!opt) return;

      setLastIndex(opt.index);
      onMatch(opt.index);
    },
    [shouldShowDropdown, filteredOptions, lastIndex, onMatch],
  );

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      selectAdjacentOption('down');
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      selectAdjacentOption('up');
      return;
    }

    if (e.key === 'Enter') {
      if (shouldShowDropdown && lastIndex !== null && lastIndex !== undefined) {
        onMatch(lastIndex);
      } else {
        runSearch('down');
      }
      setIsOpen(false);
    }

    if (e.key === 'Escape') setIsOpen(false);
  };

  const disableButtons = disabled || !query.trim();

  return (
    <div
      className={
        'input search-input' +
        (className ? ' ' + className : '') +
        (disabled ? ' disabled' : '')
      }
    >
      <input
        ref={setReference}
        type='text'
        value={query}
        placeholder={placeholder}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (disabled) return;
          if (query.trim()) setIsOpen(true);
        }}
        onClick={() => {
          if (disabled) return;
          if (query.trim()) setIsOpen(true);
        }}
        disabled={disabled}
        className={small ? 'small' : ''}
      />

      <div className='search-controls'>
        {!query.trim() ? (
          <p
            className={'search-icon fa-search' + (small ? ' small' : '')}
            aria-hidden='true'
          />
        ) : (
          <>
            <button
              type='button'
              className={'search-btn up' + (small ? ' small' : '')}
              onClick={() => runSearch('up')}
              aria-label='Previous match'
              disabled={disableButtons}
            >
              <p className='fa-chevron-up' />
            </button>
            <button
              type='button'
              className={'search-btn down' + (small ? ' small' : '')}
              onClick={() => runSearch('down')}
              aria-label='Next match'
              disabled={disableButtons}
            >
              <p className='fa-chevron-down' />
            </button>
          </>
        )}
      </div>

      <p className={'label' + (query ? ' visible' : '')}>{placeholder}</p>

      <FloatingPortal>
        <div
          ref={refs.setFloating}
          style={floatingStyles}
          className={
            'search-floating' +
            (shouldShowDropdown ? ' open' : '') +
            (small ? ' small' : '')
          }
          role='listbox'
          aria-label={placeholder || 'Search'}
        >
          {shouldShowDropdown && (
            <ul className='search-options'>
              {filteredOptions.map((opt) => (
                <li
                  key={opt.index}
                  role='option'
                  aria-selected={opt.index === lastIndex}
                  tabIndex={0}
                  className={opt.index === lastIndex ? 'active' : ''}
                  onClick={() => selectOption(opt)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      selectOption(opt);
                    }
                    if (e.key === 'Escape') setIsOpen(false);
                  }}
                >
                  {opt.label}
                </li>
              ))}
            </ul>
          )}
        </div>
      </FloatingPortal>
    </div>
  );
}
